/**
 * DerelictSystem.js
 *
 * Phase 3 (June 2026) — Derelict Ships & Shipwrecks.
 *
 * Two responsibilities (mirrors CaveSystem):
 *   1) MARKERS — place exterior wreck-hull markers for the derelicts the backend
 *      reports at the current location. Space wrecks drift in the system view;
 *      surface wrecks lie crashed on the terrain. The player approaches and
 *      presses E to BOARD.
 *   2) INTERIOR — a self-contained, walkable wreck hull built at a far world
 *      offset (so it never overlaps the surface terrain or the cave/station
 *      interiors). Contains lootable containers, a glowing ship's-log terminal,
 *      a glowing exit hatch and (for guarded wrecks) hostile guard-drone
 *      spawn points.
 *
 * The backend (`/api/derelicts/*`) owns determinism + persistence; this system
 * is purely visual + interaction. Container/salvage/clear state lives server
 * side and is reflected back through the React overlay.
 *
 * Public API:
 *   setDerelicts(list, location, getTerrainHeight)  // (re)place exterior markers
 *   getNearDerelict(radius)        // -> { id, name, distance } | null  (un-boarded)
 *   getDerelictPois()              // radar POIs for exterior markers
 *   buildInterior(derelict)        // construct the walkable hull (call on board)
 *   clearInterior()                // dispose the hull (call on exit)
 *   showInterior(visible)
 *   floorHeight(x,z) / clamp(x,z)  // keep the player on the deck inside the hull
 *   getEntryPoint() / getExitPos() / getNearExit(radius)
 *   getNearContainer(radius)       // -> { id, label, is_module, distance } | null
 *   markContainerLooted(id)
 *   getNearLog(radius)             // -> { distance } | null
 *   getGuardSpawns()               // [Vector3] guard-drone spawn points
 *   tick(dt)
 *   dispose()
 */

import {
  Vector3,
  Color3,
  MeshBuilder,
  StandardMaterial,
  TransformNode,
  PointLight,
  Mesh,
} from '@babylonjs/core';

// Interior lives far from everything else (cave=5000, station=8000, ship=12000).
const DX = 9000;
const DZ = 9000;
const HALF_W = 5.5;          // hull half-width (x)
const Z_MIN = -16;           // entry end
const Z_MAX = 14;            // bridge end
const FLOOR_Y = 0;
const ENTER_RADIUS = 7.0;    // board prompt range (exterior)
const EXIT_RADIUS = 4.5;
const CONTAINER_RADIUS = 3.2;
const LOG_RADIUS = 3.4;

export default class DerelictSystem {
  constructor(scene, opts = {}) {
    this.scene = scene;
    this.getPlayerPos = opts.getPlayerPos || (() => new Vector3(0, 0, 0));
    this.glow = opts.glow || null;

    this.DX = DX;
    this.DZ = DZ;

    this._markersRoot = new TransformNode('derelictMarkersRoot', scene);
    this._interiorRoot = null;
    this._markers = [];          // [{ id, name, pos:Vector3, info, boarded }]
    this._location = 'surface';

    // interior interaction targets (world coords)
    this._containers = [];       // [{ id, label, is_module, pos:Vector3, looted, mesh }]
    this._logPos = new Vector3(DX, 0, DZ);
    this._exitPos = new Vector3(DX, 0, DZ);
    this._entryPos = new Vector3(DX, 0, DZ);
    this._guardSpawns = [];      // [Vector3]
    this._lights = [];           // [{ light, base, phase }]
    this._t = 0;
  }

  // ------------------------------------------------------------------ markers

  setDerelicts(list, location = 'surface', getTerrainHeight = () => 0) {
    this.clearMarkers();
    this._location = location;
    if (!Array.isArray(list)) return;
    for (const d of list) {
      const p = d.pos || { x: 0, y: 0, z: 0 };
      const y = location === 'surface' ? (getTerrainHeight(p.x, p.z) || 0) : (p.y || 0);
      const pos = new Vector3(p.x, y, p.z);
      const mesh = this._buildMarkerMesh(d, location);
      mesh.position.copyFrom(pos);
      if (location === 'surface') mesh.rotation.z = 0.26; // crashed list
      this._markers.push({ id: d.id, name: d.name || 'Derelict', pos, info: d, mesh, boarded: false });
    }
  }

  _buildMarkerMesh(d, location) {
    const root = new TransformNode(`derelictMarker_${d.id}`, this.scene);
    root.parent = this._markersRoot;
    const big = d.size === 'large' ? 1.5 : d.size === 'medium' ? 1.15 : 0.85;

    const hullMat = new StandardMaterial(`dHullMat_${d.id}`, this.scene);
    hullMat.diffuseColor = new Color3(0.24, 0.25, 0.29);
    hullMat.specularColor = new Color3(0.08, 0.08, 0.1);

    // Main fuselage — a battered box.
    const body = MeshBuilder.CreateBox(`dHull_${d.id}`, { width: 4 * big, height: 3 * big, depth: 11 * big }, this.scene);
    body.parent = root;
    body.material = hullMat;
    body.isPickable = false;

    // Broken nose / snapped section.
    const nose = MeshBuilder.CreateCylinder(`dNose_${d.id}`, { diameterTop: 0, diameterBottom: 3.4 * big, height: 4 * big, tessellation: 7 }, this.scene);
    nose.rotation.x = Math.PI / 2;
    nose.position.z = 7.0 * big;
    nose.rotation.z = 0.35;
    nose.parent = root;
    nose.material = hullMat;
    nose.isPickable = false;

    // Engine block at the rear.
    const eng = MeshBuilder.CreateCylinder(`dEng_${d.id}`, { diameter: 3.4 * big, height: 2.6 * big, tessellation: 10 }, this.scene);
    eng.rotation.x = Math.PI / 2;
    eng.position.z = -6.2 * big;
    eng.parent = root;
    eng.material = hullMat;
    eng.isPickable = false;

    // Stubby wing.
    const wing = MeshBuilder.CreateBox(`dWing_${d.id}`, { width: 9 * big, height: 0.4 * big, depth: 3 * big }, this.scene);
    wing.position.set(0, -0.4 * big, -1 * big);
    wing.rotation.y = 0.08;
    wing.parent = root;
    wing.material = hullMat;
    wing.isPickable = false;

    // Distress beacon — guarded wrecks pulse red, others amber.
    const beaconCol = d.guarded ? new Color3(1, 0.18, 0.16) : new Color3(1, 0.62, 0.2);
    const beaconMat = new StandardMaterial(`dBeaconMat_${d.id}`, this.scene);
    beaconMat.emissiveColor = beaconCol;
    beaconMat.diffuseColor = beaconCol.scale(0.3);
    beaconMat.specularColor = new Color3(0, 0, 0);
    const beacon = MeshBuilder.CreateSphere(`dBeacon_${d.id}`, { diameter: 0.9 * big, segments: 10 }, this.scene);
    beacon.position.set(0, 2.0 * big, 1.5 * big);
    beacon.parent = root;
    beacon.material = beaconMat;
    beacon.isPickable = false;
    if (this.glow) { try { this.glow.addIncludedOnlyMesh?.(beacon); } catch (_e) { /* noop */ } }

    const light = new PointLight(`dBeaconLight_${d.id}`, new Vector3(0, 3.0 * big, 1.5 * big), this.scene);
    light.diffuse = beaconCol;
    light.intensity = 0.6;
    light.range = location === 'space' ? 40 : 26;
    light.parent = root;
    this._lights.push({ light, base: 0.6, phase: Math.random() * Math.PI * 2 });

    root.metadata = { derelictId: d.id };
    return root;
  }

  getNearDerelict(radius) {
    if (this._markers.length === 0) return null;
    const r = radius != null ? radius : (this._location === 'space' ? 45 : ENTER_RADIUS);
    const p = this.getPlayerPos();
    let best = null;
    let bestDist = r;
    for (const m of this._markers) {
      if (m.boarded) continue;
      const d = this._location === 'space'
        ? Math.hypot(p.x - m.pos.x, p.y - m.pos.y, p.z - m.pos.z)
        : Math.hypot(p.x - m.pos.x, p.z - m.pos.z);
      if (d < bestDist) { bestDist = d; best = { id: m.id, name: m.name, distance: d }; }
    }
    return best;
  }

  getMarker(id) { return this._markers.find((m) => m.id === id) || null; }

  getDerelictPois() {
    return this._markers.map((m) => ({ id: m.id, kind: 'derelict', x: m.pos.x, z: m.pos.z }));
  }

  clearMarkers() {
    for (const m of this._markers) {
      try { m.mesh.dispose(false, true); } catch (_e) { /* noop */ }
    }
    this._markers = [];
    this._lights = this._lights.filter((l) => !l._interior);
  }

  // ----------------------------------------------------------------- interior

  buildInterior(derelict) {
    this.clearInterior();
    const root = new TransformNode('derelictInteriorRoot', this.scene);
    this._interiorRoot = root;
    this._containers = [];
    this._guardSpawns = [];

    const wallMat = new StandardMaterial('dintWallMat', this.scene);
    wallMat.diffuseColor = new Color3(0.18, 0.19, 0.23);
    wallMat.specularColor = new Color3(0.03, 0.03, 0.04);
    wallMat.maxSimultaneousLights = 8;

    const floorMat = new StandardMaterial('dintFloorMat', this.scene);
    floorMat.diffuseColor = new Color3(0.12, 0.13, 0.16);
    floorMat.specularColor = new Color3(0.02, 0.02, 0.03);
    floorMat.maxSimultaneousLights = 8;

    const span = Z_MAX - Z_MIN;
    const midZ = (Z_MAX + Z_MIN) / 2;

    // Deck
    const floor = MeshBuilder.CreateGround('dintFloor', { width: HALF_W * 2, height: span, subdivisions: 1 }, this.scene);
    floor.position.set(DX, FLOOR_Y, DZ + midZ);
    floor.parent = root;
    floor.material = floorMat;
    floor.isPickable = false;
    floor.receiveShadows = true;

    // Ceiling
    const ceil = MeshBuilder.CreateGround('dintCeil', { width: HALF_W * 2, height: span, subdivisions: 1 }, this.scene);
    ceil.position.set(DX, FLOOR_Y + 5, DZ + midZ);
    ceil.rotation.x = Math.PI;
    ceil.parent = root;
    ceil.material = wallMat;
    ceil.isPickable = false;

    // Side walls
    for (const sx of [-1, 1]) {
      const w = MeshBuilder.CreateBox('dintSideWall', { width: 0.3, height: 5, depth: span }, this.scene);
      w.position.set(DX + sx * HALF_W, FLOOR_Y + 2.5, DZ + midZ);
      w.parent = root;
      w.material = wallMat;
      w.isPickable = false;
    }
    // End walls (entry end has a doorway gap handled by the hatch; build solid + frame)
    for (const sz of [Z_MIN, Z_MAX]) {
      const w = MeshBuilder.CreateBox('dintEndWall', { width: HALF_W * 2, height: 5, depth: 0.3 }, this.scene);
      w.position.set(DX, FLOOR_Y + 2.5, DZ + sz);
      w.parent = root;
      w.material = wallMat;
      w.isPickable = false;
    }

    // Bridge console + viewport (far +Z end)
    const console0 = MeshBuilder.CreateBox('dintConsole', { width: 4, height: 1.1, depth: 0.9 }, this.scene);
    console0.position.set(DX, FLOOR_Y + 0.55, DZ + Z_MAX - 1.6);
    console0.parent = root;
    console0.material = wallMat;
    console0.isPickable = false;

    // --- Ship's-log terminal (glowing) ---
    const logMat = new StandardMaterial('dintLogMat', this.scene);
    logMat.emissiveColor = new Color3(0.2, 0.95, 1.0);
    logMat.diffuseColor = new Color3(0.05, 0.2, 0.25);
    logMat.specularColor = new Color3(0, 0, 0);
    const logScreen = MeshBuilder.CreateBox('dintLogScreen', { width: 1.5, height: 1.0, depth: 0.12 }, this.scene);
    logScreen.position.set(DX, FLOOR_Y + 1.7, DZ + Z_MAX - 1.2);
    logScreen.parent = root;
    logScreen.material = logMat;
    logScreen.isPickable = false;
    if (this.glow) { try { this.glow.addIncludedOnlyMesh?.(logScreen); } catch (_e) { /* noop */ } }
    this._logPos = new Vector3(DX, FLOOR_Y, DZ + Z_MAX - 2.6);
    const logLight = new PointLight('dintLogLight', new Vector3(DX, FLOOR_Y + 2.4, DZ + Z_MAX - 2), this.scene);
    logLight.diffuse = new Color3(0.3, 0.9, 1.0);
    logLight.intensity = 0.8;
    logLight.range = 14;
    logLight.parent = root;
    this._lights.push({ light: logLight, base: 0.8, phase: 0, _interior: true });

    // --- Containers (lootable) ---
    const cs = derelict.containers || [];
    const lane = HALF_W - 1.0;
    cs.forEach((c, i) => {
      const side = i % 2 === 0 ? -1 : 1;
      const z = Z_MIN + 4 + ((i / Math.max(1, cs.length)) * (span - 9));
      const cm = new StandardMaterial(`dintCrateMat_${c.id}`, this.scene);
      if (c.is_module) {
        cm.diffuseColor = new Color3(0.2, 0.35, 0.45);
        cm.emissiveColor = new Color3(0.1, 0.45, 0.6);
      } else {
        cm.diffuseColor = new Color3(0.4, 0.34, 0.18);
        cm.emissiveColor = new Color3(0.08, 0.06, 0.02);
      }
      cm.specularColor = new Color3(0.05, 0.05, 0.05);
      cm.maxSimultaneousLights = 8;
      const crate = MeshBuilder.CreateBox(`dintCrate_${c.id}`, { width: 1.2, height: 1.0, depth: 1.2 }, this.scene);
      const pos = new Vector3(DX + side * lane, FLOOR_Y + 0.5, DZ + z);
      crate.position.copyFrom(pos);
      crate.rotation.y = (i * 0.7) % Math.PI;
      crate.parent = root;
      crate.material = cm;
      crate.isPickable = false;
      if (c.is_module && this.glow) { try { this.glow.addIncludedOnlyMesh?.(crate); } catch (_e) { /* noop */ } }
      this._containers.push({ id: c.id, label: c.label, is_module: !!c.is_module, pos, looted: !!c.looted, mesh: crate });
      if (c.looted) this._applyLootedLook(this._containers[this._containers.length - 1]);
    });

    // --- Exit hatch (glowing green pad at entry end) ---
    const pad = MeshBuilder.CreateTorus('dintExitPad', { diameter: 4, thickness: 0.35, tessellation: 24 }, this.scene);
    this._exitPos = new Vector3(DX, FLOOR_Y, DZ + Z_MIN + 2.4);
    pad.position.set(DX, FLOOR_Y + 0.12, DZ + Z_MIN + 2.4);
    pad.parent = root;
    const padMat = new StandardMaterial('dintExitPadMat', this.scene);
    padMat.emissiveColor = new Color3(0.2, 1.0, 0.45);
    padMat.diffuseColor = new Color3(0.05, 0.2, 0.1);
    padMat.specularColor = new Color3(0, 0, 0);
    pad.material = padMat;
    pad.isPickable = false;
    if (this.glow) { try { this.glow.addIncludedOnlyMesh?.(pad); } catch (_e) { /* noop */ } }
    const exitLight = new PointLight('dintExitLight', new Vector3(DX, FLOOR_Y + 2.2, DZ + Z_MIN + 2.4), this.scene);
    exitLight.diffuse = new Color3(0.3, 1.0, 0.5);
    exitLight.intensity = 0.6;
    exitLight.range = 12;
    exitLight.parent = root;
    this._lights.push({ light: exitLight, base: 0.6, phase: 1.1, _interior: true });

    // Dim flickering emergency lights down the corridor.
    for (let i = 0; i < 3; i++) {
      const z = Z_MIN + 6 + i * 8;
      const el = new PointLight(`dintEmer_${i}`, new Vector3(DX, FLOOR_Y + 4.2, DZ + z), this.scene);
      el.diffuse = new Color3(1.0, 0.4, 0.3);
      el.intensity = 0.35;
      el.range = 12;
      el.parent = root;
      this._lights.push({ light: el, base: 0.35, phase: i * 1.7, _interior: true, flicker: true });
    }

    // Entry point — just inside the hatch, facing the bridge (+Z).
    this._entryPos = new Vector3(DX, FLOOR_Y, DZ + Z_MIN + 4.5);

    // Guard spawn points (only relevant if guarded & not cleared).
    if (derelict.guarded) {
      const n = derelict.guard_count || 3;
      for (let i = 0; i < n; i++) {
        const z = Z_MIN + 7 + (i / Math.max(1, n)) * (span - 12);
        const x = DX + ((i % 2 === 0) ? -2.2 : 2.2);
        this._guardSpawns.push(new Vector3(x, FLOOR_Y, DZ + z));
      }
    }

    return root;
  }

  _applyLootedLook(c) {
    if (!c?.mesh?.material) return;
    try {
      c.mesh.material.emissiveColor = new Color3(0, 0, 0);
      c.mesh.material.diffuseColor = new Color3(0.16, 0.16, 0.18);
      c.mesh.scaling.y = 0.45;
      c.mesh.position.y = FLOOR_Y + 0.22;
    } catch (_e) { /* noop */ }
  }

  // floor is a flat deck.
  floorHeight() { return FLOOR_Y; }

  clamp(x, z) {
    const lx = x - DX;
    const lz = z - DZ;
    const cx = Math.max(-(HALF_W - 1.0), Math.min(HALF_W - 1.0, lx));
    const cz = Math.max(Z_MIN + 1.2, Math.min(Z_MAX - 1.2, lz));
    return { x: DX + cx, z: DZ + cz };
  }

  getEntryPoint() { return this._entryPos.clone(); }
  getExitPos() { return this._exitPos.clone(); }

  getNearExit(radius = EXIT_RADIUS) {
    const p = this.getPlayerPos();
    const d = Math.hypot(p.x - this._exitPos.x, p.z - this._exitPos.z);
    return d < radius ? { distance: d } : null;
  }

  getNearContainer(radius = CONTAINER_RADIUS) {
    const p = this.getPlayerPos();
    let best = null;
    let bestDist = radius;
    for (const c of this._containers) {
      if (c.looted) continue;
      const d = Math.hypot(p.x - c.pos.x, p.z - c.pos.z);
      if (d < bestDist) { bestDist = d; best = { id: c.id, label: c.label, is_module: c.is_module, distance: d }; }
    }
    return best;
  }

  markContainerLooted(id) {
    const c = this._containers.find((x) => x.id === id);
    if (c) { c.looted = true; this._applyLootedLook(c); }
  }

  getNearLog(radius = LOG_RADIUS) {
    const p = this.getPlayerPos();
    const d = Math.hypot(p.x - this._logPos.x, p.z - this._logPos.z);
    return d < radius ? { distance: d } : null;
  }

  getGuardSpawns() { return this._guardSpawns.map((s) => s.clone()); }

  showInterior(visible) {
    if (this._interiorRoot) this._interiorRoot.setEnabled(!!visible);
  }

  clearInterior() {
    if (this._interiorRoot) {
      try { this._interiorRoot.dispose(false, true); } catch (_e) { /* noop */ }
      this._interiorRoot = null;
    }
    this._containers = [];
    this._guardSpawns = [];
    this._lights = this._lights.filter((l) => !l._interior);
  }

  setMarkerBoarded(id, v) {
    const m = this.getMarker(id);
    if (m) m.boarded = !!v;
  }

  tick(dt) {
    this._t += dt;
    for (const l of this._lights) {
      if (!l.light) continue;
      if (l.flicker) {
        const f = Math.sin(this._t * 9 + l.phase) > 0.7 ? 0.1 : 1.0;
        l.light.intensity = l.base * f * (0.7 + 0.3 * Math.sin(this._t * 2 + l.phase));
      } else {
        l.light.intensity = l.base * (0.75 + 0.25 * Math.sin(this._t * 2.2 + l.phase));
      }
    }
  }

  dispose() {
    this.clearMarkers();
    this.clearInterior();
    try { this._markersRoot.dispose(); } catch (_e) { /* noop */ }
  }
}
